/**
 * Ground contact detection using ankle/wrist velocity + ground-proximity heuristics.
 *
 * A limb is "in contact" when:
 * 1. Its velocity is below a threshold (it's stationary or decelerating)
 * 2. It's near the ground line (the lowest visible landmarks in the frame)
 * 3. It hasn't been stationary for too long (stale timeout — we only care about
 *    contact that originates from movement, not a person standing still)
 *
 * Tracks ankles (27/28) and wrists (15/16) — these are more reliably detected
 * than heel/toe/finger landmarks.
 *
 * This module is intentionally self-contained for easy removal or replacement.
 */
import type { NormalizedLandmark } from "@mediapipe/tasks-vision";

// MediaPipe landmark indices — major joints only
const LEFT_ANKLE = 27;
const RIGHT_ANKLE = 28;
const LEFT_WRIST = 15;
const RIGHT_WRIST = 16;

// All tracked indices for ground line detection
const ALL_CONTACT_INDICES = [LEFT_ANKLE, RIGHT_ANKLE, LEFT_WRIST, RIGHT_WRIST];

// Tuning constants
const VELOCITY_THRESHOLD = 0.012; // normalized units per frame
const GROUND_PROXIMITY = 0.10;    // how close to the ground line (normalized Y) a limb must be
const FRAMES_ON = 3;              // frames of stillness near ground before contact activates
const FRAMES_OFF = 5;             // frames of movement before contact deactivates
const STALE_FRAMES = 60;          // ~2 seconds at 30fps — hide contact after this long
const MIN_VISIBILITY = 0.3;

export interface ContactState {
  leftFoot: boolean;
  rightFoot: boolean;
  leftHand: boolean;
  rightHand: boolean;
}

export interface ContactPoint {
  x: number;
  y: number;
}

interface LimbTracker {
  stillCount: number;
  movingCount: number;
  inContact: boolean;
  contactDuration: number; // how long contact has been active (for stale detection)
}

const EMPTY_TRACKER: LimbTracker = { stillCount: 0, movingCount: 0, inContact: false, contactDuration: 0 };

export class ContactDetector {
  private leftFoot: LimbTracker = { ...EMPTY_TRACKER };
  private rightFoot: LimbTracker = { ...EMPTY_TRACKER };
  private leftHand: LimbTracker = { ...EMPTY_TRACKER };
  private rightHand: LimbTracker = { ...EMPTY_TRACKER };
  private prevLandmarks: NormalizedLandmark[] | null = null;

  /** Call once per detection frame with the new landmarks. Returns current contact state. */
  update(landmarks: NormalizedLandmark[]): ContactState {
    const prev = this.prevLandmarks;
    this.prevLandmarks = landmarks;

    if (!prev) {
      return { leftFoot: false, rightFoot: false, leftHand: false, rightHand: false };
    }

    const groundY = findGroundY(landmarks);

    this.leftFoot = updateTracker(
      this.leftFoot,
      landmarkVelocity(prev, landmarks, LEFT_ANKLE),
      landmarkVisible(landmarks, LEFT_ANKLE),
      nearGround(landmarks, groundY, LEFT_ANKLE),
    );
    this.rightFoot = updateTracker(
      this.rightFoot,
      landmarkVelocity(prev, landmarks, RIGHT_ANKLE),
      landmarkVisible(landmarks, RIGHT_ANKLE),
      nearGround(landmarks, groundY, RIGHT_ANKLE),
    );
    this.leftHand = updateTracker(
      this.leftHand,
      landmarkVelocity(prev, landmarks, LEFT_WRIST),
      landmarkVisible(landmarks, LEFT_WRIST),
      nearGround(landmarks, groundY, LEFT_WRIST),
    );
    this.rightHand = updateTracker(
      this.rightHand,
      landmarkVelocity(prev, landmarks, RIGHT_WRIST),
      landmarkVisible(landmarks, RIGHT_WRIST),
      nearGround(landmarks, groundY, RIGHT_WRIST),
    );

    return {
      leftFoot: this.leftFoot.inContact,
      rightFoot: this.rightFoot.inContact,
      leftHand: this.leftHand.inContact,
      rightHand: this.rightHand.inContact,
    };
  }

  /** Get the canvas position for a foot contact indicator. */
  getFootContactPoint(landmarks: NormalizedLandmark[], side: "left" | "right"): ContactPoint | null {
    const idx = side === "left" ? LEFT_ANKLE : RIGHT_ANKLE;
    return landmarkPoint(landmarks, idx);
  }

  /** Get the canvas position for a hand contact indicator. */
  getHandContactPoint(landmarks: NormalizedLandmark[], side: "left" | "right"): ContactPoint | null {
    const idx = side === "left" ? LEFT_WRIST : RIGHT_WRIST;
    return landmarkPoint(landmarks, idx);
  }

  reset(): void {
    this.leftFoot = { ...EMPTY_TRACKER };
    this.rightFoot = { ...EMPTY_TRACKER };
    this.leftHand = { ...EMPTY_TRACKER };
    this.rightHand = { ...EMPTY_TRACKER };
    this.prevLandmarks = null;
  }
}

function landmarkPoint(landmarks: NormalizedLandmark[], idx: number): ContactPoint | null {
  const lm = landmarks[idx];
  if (!lm || (lm.visibility ?? 0) < MIN_VISIBILITY) return null;
  return { x: lm.x, y: lm.y };
}

/** Find the approximate ground Y — the max Y among all tracked landmarks. */
function findGroundY(landmarks: NormalizedLandmark[]): number {
  let maxY = 0;
  for (const idx of ALL_CONTACT_INDICES) {
    const lm = landmarks[idx];
    if (lm && (lm.visibility ?? 0) >= MIN_VISIBILITY) {
      if (lm.y > maxY) maxY = lm.y;
    }
  }
  return maxY;
}

/** Check if a landmark's Y is within GROUND_PROXIMITY of the ground line. */
function nearGround(landmarks: NormalizedLandmark[], groundY: number, idx: number): boolean {
  const lm = landmarks[idx];
  if (!lm) return false;
  return groundY - lm.y <= GROUND_PROXIMITY;
}

function landmarkVelocity(
  prev: NormalizedLandmark[],
  curr: NormalizedLandmark[],
  idx: number,
): number {
  const p = prev[idx];
  const c = curr[idx];
  if (!p || !c) return Infinity;
  return Math.sqrt((c.x - p.x) ** 2 + (c.y - p.y) ** 2);
}

function landmarkVisible(landmarks: NormalizedLandmark[], idx: number): boolean {
  const lm = landmarks[idx];
  if (!lm) return false;
  return (lm.visibility ?? 0) >= MIN_VISIBILITY;
}

function updateTracker(
  tracker: LimbTracker,
  velocity: number,
  visible: boolean,
  isNearGround: boolean,
): LimbTracker {
  if (!visible) {
    return { ...EMPTY_TRACKER };
  }

  const isStill = velocity < VELOCITY_THRESHOLD && isNearGround;
  let { stillCount, movingCount, inContact, contactDuration } = tracker;

  if (isStill) {
    stillCount++;
    movingCount = 0;
    if (stillCount >= FRAMES_ON) {
      inContact = true;
    }
  } else {
    stillCount = 0;
    movingCount++;
    if (movingCount >= FRAMES_OFF) {
      inContact = false;
    }
  }

  // Track how long contact has been active; hide if stale
  if (inContact) {
    contactDuration++;
    if (contactDuration >= STALE_FRAMES) {
      inContact = false;
    }
  } else {
    contactDuration = 0;
  }

  return { stillCount, movingCount, inContact, contactDuration };
}
